import { useState, useEffect, useCallback, Fragment } from "react";
import { C, S } from "../customers/theme";
import { getCreditNotes, getCreditNote, getCreditNoteStats, issueCreditNote, cancelDraft, CN_STATUSES } from "../lib/creditNotes";
import CreditNoteView from "./CreditNoteView";

const PAGE_SIZE = 25;
const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthLabel = (iso) => { if (!iso) return "No date"; const [y, m] = iso.slice(0, 7).split("-"); return `${MONTHS[+m - 1] || "?"} ${y}`; };
const statusMeta = (v) => CN_STATUSES.find((s) => s.value === v) || { label: v, color: C.textDim };

function Th({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  return (
    <th style={S.th} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${color}`, borderRadius: 10, padding: "12px 16px", minWidth: 150, flex: "1 1 150px" }}>
      <div style={{ color: C.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function CNStatusBadge({ status }) {
  const s = statusMeta(status);
  return <span style={S.pill(s.color)}>{s.label}</span>;
}

export default function CreditNoteManager({ openId, onOpenHandled }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [{ data, count: total }, s] = await Promise.all([
        getCreditNotes({ search, status, page, pageSize: PAGE_SIZE, sortBy, sortDir }),
        getCreditNoteStats(),
      ]);
      setRows(data); setCount(total); setStats(s);
    } catch (e) { setError(e.message || "Could not load credit notes."); }
    finally { setLoading(false); }
  }, [search, status, sortBy, sortDir, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    if (!openId) return;
    (async () => {
      try { const cn = await getCreditNote(openId); if (cn) setViewing(cn); }
      catch (e) { setError(e.message); } finally { onOpenHandled && onOpenHandled(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId]);

  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setPage(1); };

  const openView = async (row) => { try { setViewing(await getCreditNote(row.id)); } catch (e) { setError(e.message); } };
  const act = async (fn) => { setActing(true); setError(""); try { await fn(); await load(); } catch (e) { setError(e.message); } finally { setActing(false); } };
  const doIssue = (cn) => {
    if (!window.confirm("Issuing is final and cannot be undone; it reduces the invoice balance and, for restock lines, returns stock to inventory. Continue?")) return;
    act(async () => { await issueCreditNote(cn.id); setViewing(await getCreditNote(cn.id)); });
  };
  const doCancel = (cn) => {
    if (!window.confirm(`Discard draft ${cn.credit_note_number || ""}? This cannot be undone.`)) return;
    act(async () => { await cancelDraft(cn.id); setViewing(null); });
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all";
  const grouped = sortBy === "date";

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Kpi label="Total Credited" value={money(stats?.issuedTotal)} sub="issued" color={C.warning} />
        <Kpi label="This Month" value={money(stats?.issuedThisMonth)} sub="issued this month" color={C.primary} />
        <Kpi label="Credit Notes" value={stats?.count ?? 0} sub="all" color={C.textDim} />
        <Kpi label="Drafts" value={stats?.draftCount ?? 0} sub={`${stats?.issuedCount ?? 0} issued`} color={C.success} />
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search CN no., customer, reason…" value={search} onChange={(e) => onSearch(e.target.value)} aria-label="Search credit notes" />
        <select style={{ ...S.input, width: "auto", minWidth: 150 }} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {CN_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} credit note{count === 1 ? "" : "s"}</span>
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="Credit No." col="number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Invoice</th>
              <th style={S.th}>Customer</th>
              <th style={S.th}>Reason</th>
              <Th label="Issue Date" col="date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Total" col="total" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={7} style={S.empty}>
                {hasFilters ? (
                  <div>No credit notes match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div></div>
                ) : (
                  <div>
                    <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No credit notes yet</div>
                    <div>Create one from an issued invoice — open the invoice and choose “Create Credit Note”.</div>
                  </div>
                )}
              </td></tr>
            ) : rows.map((cn, i) => {
              const showMonth = grouped && (i === 0 || monthLabel(cn.issue_date) !== monthLabel(rows[i - 1].issue_date));
              return (
                <Fragment key={cn.id}>
                  {showMonth && (
                    <tr><td colSpan={7} style={{ padding: "10px 12px 6px", color: C.textMuted, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.05em", background: C.surface }}>{monthLabel(cn.issue_date)}</td></tr>
                  )}
                  <tr style={{ cursor: "pointer" }} onClick={() => openView(cn)}>
                    <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{cn.credit_note_number || "—"}</span></td>
                    <td style={S.td}><span style={{ fontFamily: "monospace", fontSize: 12 }}>{cn.invoices?.invoice_number || "—"}</span></td>
                    <td style={S.td}>{cn.customer_name || <span style={{ color: C.textDim }}>—</span>}</td>
                    <td style={S.td}>{(cn.reason || "—").replace(/_/g, " ")}</td>
                    <td style={S.td}>{cn.issue_date || "—"}</td>
                    <td style={S.td}><CNStatusBadge status={cn.status} /></td>
                    <td style={S.td}><strong>{money(cn.total_amount)}</strong></td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {viewing && (
        <CreditNoteView creditNote={viewing} busy={acting} onClose={() => setViewing(null)} onIssue={doIssue} onCancel={doCancel} />
      )}
    </div>
  );
}
